import React, { useState, useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import ConnectingContinents from '../assets/videos/1.mp4';
import ThinkBig from '../assets/videos/2.mp4';
import OARW from '../assets/videos/3.mp4';
import ReadyConfidence from '../assets/videos/4.mp4';
import SliderBlue from '../assets/hero-slider-icon-blue.png';
import SliderWhite from '../assets/hero-slider-icon-white.png';

const TypewriterText = ({ text, isActive }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setDisplayedText('');
      setCurrentIndex(0);
      return;
    }

    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, 40);

      return () => clearTimeout(timeout);
    }
  }, [text, currentIndex, isActive]);

  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
};

const HeroSection = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const swiperRef = useRef(null);
  const sectionRef = useRef(null);

  // UPDATED SLIDES
  const slides = [
    {
      video: ThinkBig,
      title: 'Why Big Blue?',
      subtitle: (
        <div className="text-left text-base md:text-xl lg:text-2xl space-y-2 mt-4 inline-block">
          <p>✔ Canada-based freight forwarder with global agent network</p>
          <p>✔ Expertise in FCL, LCL, Air & Project Cargo</p>
          <p>✔ Strong experience with CN / CP rail & port operations</p>
          <p>✔ Transparent pricing & proactive communication</p>
          <p>✔ Personalized service (not call-center logistics)</p>
        </div>
      ),
    },
    {
      video: ConnectingContinents,
      title: 'Technology Driven + Visibility Focused',
      subtitle: (
        <div className="mt-4">
          <p className="font-bold text-lg md:text-2xl mb-2">Complete Door-to-Door Visibility. One Click. Total Control.</p>
          <p className="text-base md:text-xl">Real-time shipment tracking, customer login access, and end-to-end visibility across ocean, air, trucking, customs, and final delivery.</p>
        </div>
      ),
    },
    {
      video: OARW,
      title: 'One-Window Solution',
      subtitle: (
        <div className="mt-4">
          <p className="font-bold text-lg md:text-2xl mb-2">One Logistics Partner. One Window. Complete Door-to-Door Solutions.</p>
          <p className="text-base md:text-xl">From buyer consolidation and ocean freight to customs, inland delivery, and final mile — Big Blue manages your entire supply chain under one roof.</p>
        </div>
      ),
    },
    {
      video: ReadyConfidence,
      title: 'Ocean Freight Specialists',
      subtitle: (
        <p className="text-base md:text-xl mt-4">
          Buyer consolidation, LCL consolidation, garment-on-hanger solutions, break bulk, project cargo and full container services — managed with precision from origin to destination.
        </p>
      ),
    },
    {
      video: ThinkBig, 
      title: 'Global Freight Forwarding & Logistics Solutions You Can Trust',
      subtitle: (
        <p className="text-lg md:text-2xl mt-4 font-bold">
          Ocean • Air • Ground • Customs • Door-to-Door • Warehousing • E-Commerce • Project Cargo
        </p>
      ),
    },
    {
      video: ConnectingContinents, 
      title: 'Think Big.',
      subtitle: (
        <p className="text-3xl md:text-5xl lg:text-6xl font-bold mt-2 font-satoshi">
          Move Blue.
        </p>
      ),
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="h-[100vh] w-full relative transition-colors duration-700"
      style={{ backgroundColor: '#02044f' }}
    >
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        pagination={false}
        autoplay={{ delay: 8000, disableOnInteraction: false }}
        loop
        className="h-full w-full"
        onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          setActiveSlide(swiper.realIndex);
        }}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <div className="h-[100vh] w-full flex items-center justify-center text-center text-white relative">
              {/* Video Background */}
              <video
                className="absolute top-0 left-0 w-full h-full object-cover z-[1]"
                src={slide.video}
                autoPlay
                loop
                muted
                playsInline
              />

              {/* Bottom Fade Overlay */}
              <div
                className="absolute top-0 left-0 w-full h-full z-[2] pointer-events-none"
                style={{
                  background: `linear-gradient(
                    to bottom,
                    rgba(2, 4, 79, 0.7) 0%,
                    rgba(2, 4, 79, 0.5) 60%,
                    rgba(2, 4, 79, 1) 100%
                  )`
                }}
              />

              {/* Slide Content */}
              <div className="p-8 rounded-lg max-w-screen-2xl max-h-screen mx-4 relative z-10 flex flex-col items-center justify-center h-full">
                
                {/* Main Title */}
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 font-satoshi">
                  <TypewriterText
                    text={slide.title}
                    isActive={activeSlide === index}
                  />
                </h1>

                {/* Subtitle (Dynamic Content) */}
                <div className="text-lg md:text-2xl font-satoshi max-w-4xl mx-auto">
                  {slide.subtitle}
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Pagination Dots */}
      <div className="absolute bottom-[5%] left-0 w-full flex items-center justify-center z-10 h-8">
        {slides.map((_, index) => (
          <button
            key={index}
            className="w-7 h-7 mx-2 flex items-center justify-center bg-transparent p-0 border-none cursor-pointer focus:outline-none"
            onClick={() => swiperRef.current.slideToLoop(index)}
            aria-label={`Go to slide ${index + 1}`}
          >
            <img
              src={activeSlide === index ? SliderBlue : SliderWhite}
              alt={`Slide ${index + 1} icon`}
              className="w-full h-full object-contain"
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSection;